/**
 * 입력 유효성 검사 및 sanitize 유틸리티
 */

// ========================================
// 유효성 검사 상수
// ========================================

// 계좌번호 유효성 규칙
const ACCOUNT_NUMBER_VALIDATION = {
    MIN_LENGTH: 10,
    MAX_LENGTH: 20,
    PATTERN: /^[0-9]*$/,
    ERROR_MESSAGES: {
        REQUIRED: '계좌번호를 입력해주세요.',
        INVALID_LENGTH: '계좌번호는 10자리 이상 20자리 이하로 입력해주세요.',
        INVALID_FORMAT: '계좌번호는 숫자만 입력 가능합니다.',
    },
} as const;

// 예금주 유효성 규칙
const ACCOUNT_HOLDER_VALIDATION = {
    MIN_LENGTH: 2,
    MAX_LENGTH: 20,
    PATTERN: /^[가-힣a-zA-Z\s]*$/,
    ERROR_MESSAGES: {
        REQUIRED: '예금주를 입력해주세요.',
        INVALID_LENGTH: '예금주는 2자리 이상 입력해주세요.',
        INVALID_FORMAT: '예금주는 한글, 영문, 공백만 입력 가능합니다.',
    },
} as const;

// 은행 선택 유효성
const BANK_VALIDATION = {
    ERROR_MESSAGES: {
        REQUIRED: '은행을 선택해주세요.',
    },
} as const;

// ========================================
// Input Sanitization 함수
// ========================================

/**
 * 계좌번호 입력값 sanitize (숫자만 허용)
 * @param value 입력값
 */
export const sanitizeAccountNumber = (value: string): string => {
    return value.replace(/[^0-9]/g, '');
};

/**
 * 예금주 입력값 sanitize (한글, 영문, 공백만 허용, 최대 길이 제한)
 * @param value 입력값
 */
export const sanitizeAccountHolder = (value: string): string => {
    // 한글(가-힣, ㄱ-ㅎ, ㅏ-ㅣ), 영문, 공백만 허용
    const sanitized = value.replace(/[^가-힣ㄱ-ㅎㅏ-ㅣa-zA-Z\s]/g, '');
    return sanitized.slice(0, ACCOUNT_HOLDER_VALIDATION.MAX_LENGTH);
};

// ========================================
// Validation 함수
// ========================================

/**
 * 계좌 정보 전체 유효성 검사
 * @param bank 은행
 * @param accountNumber 계좌번호
 * @param accountHolder 예금주
 * @returns 에러 메시지 (없으면 null)
 */
export const validateAccountInfo = (
    bank: string,
    accountNumber: string,
    accountHolder: string
): string | null => {
    // 은행 선택 체크
    if (!bank || bank.trim() === '') {
        return BANK_VALIDATION.ERROR_MESSAGES.REQUIRED;
    }

    // 계좌번호 체크
    if (!accountNumber || accountNumber.trim() === '') {
        return ACCOUNT_NUMBER_VALIDATION.ERROR_MESSAGES.REQUIRED;
    }

    // 계좌번호 길이 체크
    if (
        accountNumber.length < ACCOUNT_NUMBER_VALIDATION.MIN_LENGTH ||
        accountNumber.length > ACCOUNT_NUMBER_VALIDATION.MAX_LENGTH
    ) {
        return ACCOUNT_NUMBER_VALIDATION.ERROR_MESSAGES.INVALID_LENGTH;
    }

    // 예금주 체크
    if (!accountHolder || accountHolder.trim() === '') {
        return ACCOUNT_HOLDER_VALIDATION.ERROR_MESSAGES.REQUIRED;
    }

    // 예금주 길이 체크
    if (accountHolder.length < ACCOUNT_HOLDER_VALIDATION.MIN_LENGTH) {
        return ACCOUNT_HOLDER_VALIDATION.ERROR_MESSAGES.INVALID_LENGTH;
    }

    return null;
};
